/**
 * Régua superior de ações em lote (não-flutuante).
 * Substitui o bucket header quando há itens selecionados.
 *
 * @version 3.1.0 - 2026-07-24 — botão de leitura vira TOGGLE dinâmico:
 *   - Se há algum item NÃO lido na seleção → "Marcar lidas" (MailOpen)
 *   - Se todos selecionados já estão lidos → "Marcar não lidas" (Mail)
 * @version 3.0.0 - 2026-04-20 — refinamento "Enterprise Premium"
 */
import { MailOpen, Mail, Archive, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  count: number;
  /** True quando pelo menos um item selecionado ainda está NÃO lido. */
  hasUnreadSelected: boolean;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onArchive: () => void;
  onClear: () => void;
}

export function NotificationBulkActionsBar({
  count,
  hasUnreadSelected,
  onMarkRead,
  onMarkUnread,
  onArchive,
  onClear,
}: Props) {
  if (count === 0) return null;

  // Toggle dinâmico: prioriza "Marcar lidas" quando há mistura ou tudo não lido.
  const readToggle = hasUnreadSelected
    ? { label: 'Marcar lidas', Icon: MailOpen, onClick: onMarkRead }
    : { label: 'Marcar não lidas', Icon: Mail, onClick: onMarkUnread };

  return (
    <div className="sticky top-0 z-20 flex items-center justify-between gap-3 px-3 py-2 border-b border-border bg-primary/[0.04] backdrop-blur-sm">
      <div className="flex items-center gap-2 text-[12px]">
        <span className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold tabular-nums">
          {count}
        </span>
        <span className="text-foreground font-medium">
          {count === 1 ? 'selecionada' : 'selecionadas'}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={readToggle.onClick}
          className="h-7 px-2 text-[12px] gap-1.5"
        >
          <readToggle.Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
          {readToggle.label}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onArchive}
          className="h-7 px-2 text-[12px] gap-1.5"
        >
          <Archive className="h-3.5 w-3.5" strokeWidth={1.75} />
          Arquivar
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onClear}
          className="h-7 w-7"
          aria-label="Cancelar seleção"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
